import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'danger'
  | 'danger-outline'
  | 'ghost'
  | 'saldo';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export type ButtonCut =
  | 'slash'
  | 'slash-sm'
  | 'cut'
  | 'cut-sm'
  | 'g-slash'
  | 'g-cut'
  | 'none';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  cut?: ButtonCut;
  isLoading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  glow?: boolean;
  fullWidth?: boolean;
}

const CUT_CLASSES: Record<ButtonCut, string> = {
  'slash': 'clip-slash',
  'slash-sm': 'clip-slash-sm',
  'cut': 'clip-cut',
  'cut-sm': 'clip-cut-sm',
  'g-slash': 'clip-g-slash',
  'g-cut': 'clip-g-cut',
  'none': 'rounded-none',
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-royal text-white hover:bg-deep active:bg-deep/90 shadow-sm hover:shadow-royal-hard border-0',
  secondary:
    'bg-white text-royal border-2 border-line hover:bg-sky-light hover:text-deep hover:border-sky-border active:bg-ice',
  outline:
    'bg-transparent text-white border-2 border-royal/80 hover:bg-royal/15 hover:border-royal active:bg-royal/25',
  danger:
    'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 border-0',
  'danger-outline':
    'bg-white text-red-600 border-2 border-red-200 hover:bg-red-50 hover:border-red-400 hover:text-red-700 active:bg-red-100',
  ghost:
    'bg-white/10 text-white border border-white/20 hover:bg-white/20 hover:border-white/40 active:bg-white/30',
  saldo:
    'bg-white text-ink border-2 border-line hover:bg-ice hover:text-royal hover:border-royal active:bg-ice-surface',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-[38px] px-3.5 text-xs gap-1.5',
  md: 'h-[44px] px-5 text-sm gap-2',
  lg: 'h-[52px] px-7 text-base gap-2.5',
  icon: 'w-[40px] h-[40px] p-0 shrink-0 gap-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant = 'primary',
      size = 'md',
      cut = 'slash',
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      glow = false,
      fullWidth = false,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={cn(
          // Base button styles
          'inline-flex items-center justify-center font-heading font-black uppercase tracking-wider',
          'transition-all duration-150 ease-out select-none whitespace-nowrap cursor-pointer',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2 focus-visible:ring-offset-fnDark',
          // Hover and active transforms
          'hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]',
          // Disabled state
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none disabled:pointer-events-none',
          // Dimensions and variant styling
          CUT_CLASSES[cut],
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          glow && 'drop-shadow-sky-hard hover:drop-shadow-[0_0_12px_rgba(92,203,255,0.7)]',
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden="true" />
            <span>{loadingText || children}</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="shrink-0 inline-flex items-center">{leftIcon}</span>}
            {children && <span>{children}</span>}
            {rightIcon && <span className="shrink-0 inline-flex items-center">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
